import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import { ProfileContext } from '../../../context/ProfileContext'; // use ProfileContext, not the profileContext variable
import { useNavigate } from 'react-router-dom';
import StyledButton from '../../../components/Button/Button';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import { Box, Container, Typography } from '@mui/material';
import ProfileDetailsInput from '../../../components/Inputs/ProfileDetailsInput';
import UploadProfilePic from '../UploadProfilePic/UploadProfilePic';
import UploadBusinessCard from '../UploadBusinessCard/UploadBusinessCard';
import { getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';

const CreateBabysitterProfile: React.FC = () => {
  const profileContext = useContext(ProfileContext);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [firstNameError, setfirstNameError] = useState<boolean>(false);
  const [lastNameError, setlastNameError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);

  if (!profileContext) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }

  // Destructure from profileContext directly, as it already contains the required functions/values
  const {
    handleNewProfile,
    newProfile,
    handlePreview,
    handleProfileStage,
    profileStage,
  } = profileContext;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '2') {
            navigate('/participant/inviteBabysitter');
          }
          if (stage === '3') {
            navigate('/participant/selectplan');
          }
          if (stage === '4') {
            navigate('/participant/dashboard');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  const formatName = (input: string) => {
    const formattedName = input.replace(/[^a-zA-Z\s]/g, '');
    return formattedName;
  };

  const formatPhoneNumber = (input: string) => {
    // Remove all non-numeric characters
    const numbersOnly = input.replace(/\D/g, '');

    // Format as XXX XXXX XXX
    const formattedNumber = numbersOnly.replace(
      /(\d{3})(\d{0,4})(\d{0,3})/,
      '$1 $2 $3',
    );

    // Trim the result to remove any trailing spaces
    return formattedNumber.trim();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, files } = event.target;
    console.log('value is ', value);

    if (type === 'file' && files && files.length > 0) {
      handleNewProfile({ [name]: files[0] });
    } else if (name === 'firstName' || name === 'lastName') {
      const trimmedValue = formatName(value).slice(0, 20);
      handleNewProfile({ [name]: trimmedValue });
    } else if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      if (numericValue === '' || /^[2-9]\d{0,9}$/.test(numericValue)) {
        handleNewProfile({ [name]: numericValue });
      }
    } else {
      handleNewProfile({ [name]: value });
    }
  };

  const handleContinue = () => {
    console.log('newProfile is ', newProfile);
    console.log('phone is ', newProfile.phone.replace(/\s+/g, '').charAt(0));
    if (newProfile.firstName.length < 3) {
      setfirstNameError(true);
      setlastNameError(false);
      setPhoneError(false);
      setErrorMsg('first name should be atleast 3 characters long');
    } else if (newProfile.lastName.length < 3) {
      setlastNameError(true);
      setfirstNameError(false);
      setPhoneError(false);
      setErrorMsg('last name should be atleast 3 characters long');
    } else if (newProfile.phone.replace(/\s+/g, '').length !== 10) {
      console.log('phone is ', newProfile.phone.replace(/\s+/g, ''));
      setPhoneError(true);
      setfirstNameError(false);
      setlastNameError(false);
      setErrorMsg('Mobile number should be 10 digits.');
    } else if (
      newProfile.phone.replace(/\s+/g, '').charAt(0) === '0' ||
      newProfile.phone.replace(/\s+/g, '').charAt(0) === '1' ||
      newProfile.phone.replace(/\s+/g, '').charAt(3) === '0' ||
      newProfile.phone.replace(/\s+/g, '').charAt(3) === '1'
    ) {
      setPhoneError(true);
      setfirstNameError(false);
      setlastNameError(false);
      setErrorMsg(
        'Phone number must be in the format NXX-NXX-XXXX, where N is 2-9 and X is 0-9.',
      );
    } else {
      setfirstNameError(false);
      setlastNameError(false);
      setPhoneError(false);
      if (newProfile.profilePic === '') {
        handlePreview(null);
      }
      handleProfileStage(2);
    }
  };

  const renderErrorMsg = () => {
    if (errorMsg) {
      return <Typography className="error1-msg">{errorMsg}</Typography>;
    }
    return null;
  };

  const backClick = () => {
    if (profileStage !== 1) {
      handleProfileStage(profileStage - 1);
    } else {
      navigate('/signup');
    }
  };

  return (
    <>
      {profileStage === 1 && (
        <StyledBgContainer>
          <TopNav showArrow={true} backClick={backClick} />
          <Container maxWidth="sm">
            <Box component="form" noValidate autoComplete="off">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  px: { sm: 6 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontFamily: 'Opensans',
                    fontWeight: '700',
                  }}
                >
                  Create Profile
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'grey', mt: 1, mb: 3 }}
                >
                  Enter required information necessary to build your personal
                  profile. Once entered, hit the &ldquo;Continue&rdquo; button
                  to move to the next step.
                </Typography>

                <ProfileDetailsInput
                  type="text"
                  value={newProfile.firstName || ''}
                  name="firstName"
                  onChange={handleChange}
                  label="First Name"
                />
                {firstNameError && (
                  <Box
                    className="signup-error"
                    sx={{ marginTop: '8px', mb: 1, pl: 2 }}
                  >
                    {renderErrorMsg()}
                  </Box>
                )}
                <ProfileDetailsInput
                  type="text"
                  value={newProfile.lastName || ''}
                  name="lastName"
                  onChange={handleChange}
                  label="Last Name"
                />
                {lastNameError && (
                  <Box
                    className="signup-error"
                    sx={{ marginTop: '8px', mb: 1, pl: 2 }}
                  >
                    {renderErrorMsg()}
                  </Box>
                )}

                <ProfileDetailsInput
                  type="text"
                  name="phone"
                  value={formatPhoneNumber(newProfile.phone) || ''}
                  onChange={handleChange}
                  label="Phone Number"
                />

                {phoneError && (
                  <Box
                    className="signup-error"
                    sx={{ marginTop: '8px', mb: 1, pl: 2 }}
                  >
                    {renderErrorMsg()}
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 7 }}>
                <StyledButton
                  onClick={handleContinue}
                  disabled={
                    !(
                      newProfile.firstName &&
                      newProfile.lastName &&
                      newProfile.phone
                    )
                  }
                >
                  Continue
                </StyledButton>
              </Box>
            </Box>
          </Container>
        </StyledBgContainer>
      )}
      {profileStage === 2 && <UploadProfilePic />}
      {profileStage === 3 && <UploadBusinessCard />}
    </>
  );
};

export default CreateBabysitterProfile;
